import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, BookOpen, Award, Users, Brain, Cpu, Globe,
  CheckCircle, Target, TrendingUp, Star, Shield, GraduationCap,
  Monitor, Clock, Briefcase, Code, Zap, Server,
  UserCheck, BarChart3, Lightbulb, ChevronRight
} from "lucide-react";
import { SEO, StatCard } from "@/components/shared";
import { MEDIA, fadeInUp, staggerContainer, viewportOnce, HERO_IMAGES } from "@/constants";
import { PHOTOS, PhotoBand, FramedPhoto } from "@/components/Photo";

const HERO_STATS = [
  { id: 'ac-modules', end: 17, suffix: "", label: "Curriculum Modules", icon: BookOpen },
  { id: 'ac-sessions', end: 41, suffix: "", label: "Sessions (2 hrs each)", icon: Clock },
  { id: 'ac-projects', end: 6, suffix: "", label: "Hands-On Projects", icon: Code },
  { id: 'ac-fee', end: 25, suffix: "K", label: "INR per student + GST", icon: Target },
];

const LEARNING_EXPERIENCE = [
  { id: 'le-live', icon: Monitor, title: "Expert-Led Live Classes", desc: "Engage in interactive live sessions with industry experts, applying theoretical knowledge to real-world problems in our lab-supported environment." },
  { id: 'le-capstone', icon: Code, title: "Capstone Projects", desc: "4 mini projects and 2 main projects to develop practical AI skills and problem-solving abilities throughout the program." },
  { id: 'le-mentorship', icon: Users, title: "Professional Mentorship", desc: "Seasoned specialists help learners explore AI career paths and improve collaborative thinking, problem-solving, and communication skills." },
  { id: 'le-cert', icon: Award, title: "Industry Certification", desc: "Participants who complete sessions, engage with expert teams, and finish projects receive an industry-recognized certification." },
  { id: 'le-internship', icon: Briefcase, title: "Internship Opportunities", desc: "Select participants may collaborate with leading professionals from our partnered industry networks, gaining hands-on experience." },
];

const MODULES = [
  { num: "01", name: "Introduction to AI", sessions: 2, hours: "4h", mode: "Virtual", topics: "Understanding & Evolution of AI, Significance in the Modern World, Applications of AI" },
  { num: "02", name: "Introduction to ML", sessions: 2, hours: "3h", mode: "Virtual", topics: "Basics of Machine Learning, Types of ML, What is Big Data?" },
  { num: "03", name: "Introduction to DL & NN", sessions: 2, hours: "3h", mode: "Virtual", topics: "Introduction to Neural Networks, Deep Learning, Applications of Deep Learning" },
  { num: "04", name: "AI Tools & Platforms", sessions: 1, hours: "2h", mode: "Classroom", topics: "AI Development Tools, Popular AI Frameworks, Cloud-Based AI Services" },
  { num: "05", name: "Orange Data Mining", sessions: 2, hours: "3h", mode: "Classroom", topics: "Demonstration of Orange Data Mining Tool, Building analysis workflows without coding" },
  { num: "06", name: "AI Ethics & Social Impact", sessions: 2, hours: "3h", mode: "Virtual", topics: "AI Regulation & Governance, India's AI Potential, Ethical Principles, Economic Impacts" },
  { num: "07", name: "Orientation", sessions: 1, hours: "2h", mode: "Virtual", topics: "Introduction to Training Journey, Expectations & Learning Outcomes, Tools & Resources" },
  { num: "08", name: "Introduction to Python", sessions: 4, hours: "8h", mode: "Classroom", topics: "Python Basics, Syntax, Data Structures, Libraries, Real-World Applications" },
  { num: "09", name: "Introduction to ML Algorithms", sessions: 4, hours: "8h", mode: "Classroom", topics: "Supervised & Unsupervised Learning, Reinforcement Learning" },
  { num: "10", name: "Introduction to DL Algorithms", sessions: 3, hours: "6h", mode: "Classroom", topics: "Neural Network & DL Recap, DL Algorithms, DL vs. ML" },
  { num: "11", name: "Natural Language Processing", sessions: 4, hours: "8h", mode: "Classroom", topics: "NLP Introduction & Key Benefits, Applications, ChatGPT in NLP: Concepts & Cases" },
  { num: "12", name: "Computer Vision & AI Apps", sessions: 3, hours: "6h", mode: "Classroom", topics: "History of CV, Human vs. Computer Vision, Applications & Key Concepts" },
  { num: "13", name: "Internet of Things - IoT", sessions: 3, hours: "6h", mode: "Classroom", topics: "IoT Introduction, Working & Technologies, Uses, Challenges & Trends" },
  { num: "14", name: "Ideation to Deployment", sessions: 3, hours: "6h", mode: "Classroom", topics: "AI Model Deployment, Cloud Deployment & Scaling, Monitoring & CI/CD" },
  { num: "15", name: "Evaluation Metrics", sessions: 2, hours: "4h", mode: "Virtual", topics: "Importance of Model Evaluation, Evaluation Metrics for Performance" },
  { num: "16", name: "GANs & Autoencoders", sessions: 4, hours: "8h", mode: "Classroom", topics: "How GANs Work, Use Cases & Applications, Autoencoders & Applications" },
  { num: "17", name: "Summary & Mentorship", sessions: 1, hours: "2h", mode: "Virtual", topics: "Recap of Key Concepts, Interactive Q&A, Project Mentoring & Career Roadmap" },
];

const OUTCOMES = [
  "Analyze data and explore ML models",
  "Evaluate AI risks, benefits, and ethics",
  "Drive economic growth through AI",
  "Apply deep learning to real-world problems",
  "Evaluate AI models with performance metrics",
  "Strengthen critical thinking in AI/ML",
];

const ROLES = [
  "Data / Business Analyst",
  "Junior Data Engineer",
  "Associate AI-ML Engineer",
  "AI Research Assistant",
  "AI Support Specialist",
  "Data Scientist",
  "Associate CV / NLP / IoT Engineer",
];

const KEY_SKILLS = [
  "Data Analysis & Visualization",
  "Machine Learning & Deep Learning",
  "NLP, Computer Vision & IoT",
  "AI Model Deployment & Scaling",
  "Python Programming",
  "Critical Thinking & Problem-Solving",
];

const TECH_STACK = [
  { name: "Python", color: "#3776AB" },
  { name: "PyTorch", color: "#EE4C2C" },
  { name: "Scikit-learn", color: "#F7931E" },
  { name: "Pandas", color: "#150458" },
  { name: "Matplotlib", color: "#11557C" },
  { name: "Jupyter", color: "#F37626" },
  { name: "Google Colab", color: "#F9AB00" },
  { name: "Orange DM", color: "#FF8C00" },
  { name: "OpenAI Codex", color: "#412991" },
  { name: "Claude AI", color: "#D4A574" },
];

const TESTIMONIALS = [
  { id: 't1', name: "Jerry Louis Leo", role: "Working Professional", text: "I really enjoyed the course. The slides were well aligned with our learning goals and the session was very informative. It was clear that a lot of thought went into organizing the material. Overall, a great learning experience!" },
  { id: 't2', name: "Aneesha", role: "Student", text: "The structured curriculum and hands-on exercises gave me a strong foundation in machine learning and data analysis. The instructor's engaging style and real-life examples made learning enjoyable and clear." },
  { id: 't3', name: "Rahul Sharma", role: "Engineering Student", text: "The AI Catalyst\u2122 program completely changed how I think about technology. The hands-on projects were the highlight \u2014 I built a working NLP model by the end of the course. Highly recommend!" },
  { id: 't4', name: "Priya Nair", role: "MBA Graduate", text: "As someone from a non-technical background, I was nervous about joining. But the instructors made every concept accessible and relatable. The ethics and business impact modules were especially eye-opening." },
];

const COUNCIL = [
  { id: 'gc-kallol', name: "Mr. Kallol Hazra", title: "SME - IT Solutions & Talent Transformation", desc: "Technology sales expert with 35+ years of experience, combining customer insights, Digital Transformation and a strong passion for people & innovation.", prev: "Dell | HP | IBM" },
  { id: 'gc-praveen', name: "Mr. Praveen Kumar", title: "SME - IT Consulting & GTM Solutions | Workforce Transformation", desc: "Certified AI Coach & Board Advisor to Higher-Ed Institutes. 22+ years of demonstrated expertise in the IT and Services industry.", prev: "Dell | NewWave" },
];

export default function AICatalystPage() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.replace('#', ''));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    }
  }, [hash]);

  return (
    <div className="pt-24">
      <SEO
        title="AI Catalyst\u2122 Certification Program"
        description="AI Catalyst\u2122 \u2014 Your Essential Edge. A comprehensive 9-month AI certification program covering 17 modules, 41 sessions, 6 hands-on projects. Industry-recognized certification by Qbic Catalyst Partners."
        keywords="AI Catalyst, AI certification, AI training program, machine learning course, deep learning, NLP, computer vision, Python, AI career, Qbic Catalyst, university AI program"
        path="/ai-catalyst"
      />

      {/* Hero */}
      <section className="relative overflow-hidden page-hero" style={{ "--hero-img": `url(${HERO_IMAGES.aiCatalyst})` }}>
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'linear-gradient(#C9A962 1px, transparent 1px), linear-gradient(90deg, #C9A962 1px, transparent 1px)', backgroundSize: '50px 50px' }} />
        <div className="absolute top-20 right-0 w-[600px] h-[400px] rounded-full opacity-[0.07]" style={{ background: 'radial-gradient(ellipse, #C9A962, transparent 70%)' }} />

        <div className="container-custom relative z-10 py-24 md:py-32">
          <motion.div initial="initial" animate="animate" variants={staggerContainer} className="max-w-4xl">
            <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 bg-[#C9A962]/10 border border-[#C9A962]/25 rounded-full px-4 py-1.5 mb-6">
              <Brain size={14} className="text-[#C9A962]" />
              <span className="text-[#C9A962] text-xs font-semibold uppercase tracking-wider">Certification Program</span>
            </motion.div>
            <motion.h1 variants={fadeInUp} className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 leading-[1.1]">
              AI Catalyst<sup className="text-[#C9A962] text-xl md:text-2xl">&trade;</sup>
            </motion.h1>
            <motion.p variants={fadeInUp} className="text-2xl md:text-3xl text-[#C9A962] font-semibold mb-6">
              Your Essential Edge
            </motion.p>
            <motion.p variants={fadeInUp} className="text-lg md:text-xl text-[#A0ABC0] mb-10 max-w-2xl leading-relaxed">
              A structured, industry-aligned AI certification program designed to empower learners with the skills that matter most in today's AI-driven world.
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-wrap gap-3 mb-12">
              {["AI Catalyst\u2122 Certification", "Industry Recognized", "Classroom & Virtual Delivery"].map((tag) => (
                <span key={tag} className="bg-[#1A2A40] border border-[#2A3648] rounded-full px-4 py-2 text-sm text-[#A0ABC0]">{tag}</span>
              ))}
            </motion.div>
            <motion.div variants={fadeInUp}>
              <Link to="/contact" className="btn-primary text-base px-10 py-4 inline-flex items-center gap-3" data-testid="ac-hero-cta">
                Partner With Us <ArrowRight size={20} />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Key Stats */}
      <section className="section bg-[#0A1628]">
        <div className="container-custom">
          <motion.div initial="initial" whileInView="animate" viewport={viewportOnce} variants={staggerContainer} className="stats-grid">
            {HERO_STATS.map((s) => (
              <StatCard key={s.id} end={s.end} suffix={s.suffix} label={s.label} icon={s.icon} />
            ))}
          </motion.div>
        </div>
      </section>

      {/* Program Overview */}
      <section id="overview" className="section theme-light tint-ivory scroll-mt-24">
        <div className="container-custom">
          <motion.div initial="initial" whileInView="animate" viewport={viewportOnce} variants={staggerContainer}>
            <motion.div variants={fadeInUp} className="grid lg:grid-cols-2 gap-14 items-center mb-14">
              <div>
              <p className="text-[#C9A962] text-sm uppercase tracking-[0.2em] mb-4">Program Overview</p>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">A Comprehensive Learning Experience</h2>
              <p className="text-[#A0ABC0] text-lg leading-relaxed">
                Every element of the AI Catalyst&trade; Certification Program is designed to deliver measurable outcomes &mdash; from live expert sessions to hands-on projects and industry-recognized credentials. The program takes participants from AI fundamentals through advanced topics including Python, ML, deep learning, NLP, computer vision, IoT, and deployment.
              </p>
              </div>
              <FramedPhoto src={PHOTOS.labTeacher} alt="Students learning hands-on in a computer lab" badge={{ value: "82 hrs", label: "Live, hands-on learning" }} testId="ac-overview-photo" />
            </motion.div>
            <motion.div variants={staggerContainer} className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {LEARNING_EXPERIENCE.map((item) => (
                <motion.div key={item.id} variants={fadeInUp} className="p-6 bg-[#0A1628] rounded-xl border border-[#2A3648] card-hover">
                  <div className="icon-container mb-5 w-14 h-14"><item.icon className="text-[#C9A962]" size={24} /></div>
                  <h4 className="text-lg font-bold mb-2">{item.title}</h4>
                  <p className="text-[#A0ABC0] text-sm">{item.desc}</p>
                </motion.div>
              ))}
            </motion.div>
            <p className="text-[#A0ABC0] text-xs mt-6 italic">* Mentorship and Internship Opportunities are available in the advanced phase. Internship placements are subject to selection criteria and partner availability.</p>
          </motion.div>
        </div>
      </section>

      {/* Curriculum Modules */}
      <section id="curriculum" className="section bg-[#0A1628] scroll-mt-24">
        <div className="container-custom">
          <motion.div initial="initial" whileInView="animate" viewport={viewportOnce} variants={staggerContainer}>
            <motion.div variants={fadeInUp} className="text-center mb-14">
              <p className="text-[#C9A962] text-sm uppercase tracking-[0.2em] mb-4">Curriculum</p>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">17 Modules &middot; 41 Sessions &middot; 82 Hours</h2>
              <p className="text-[#A0ABC0] text-lg max-w-2xl mx-auto">Covering the full AI learning journey from fundamentals to advanced deployment.</p>
            </motion.div>

            {/* Module Grid */}
            <motion.div variants={staggerContainer} className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {MODULES.map((m) => (
                <motion.div key={m.num} variants={fadeInUp} className="p-5 bg-[#121E30] rounded-lg border border-[#2A3648] hover:border-[#C9A962]/30 transition-colors">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[#C9A962] text-xs font-mono font-bold">MODULE {m.num}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-[#A0ABC0] bg-[#0A1628] rounded px-2 py-0.5">{m.sessions}S &middot; {m.hours}</span>
                      <span className={`text-xs rounded px-2 py-0.5 ${m.mode === 'Virtual' ? 'bg-blue-500/10 text-blue-400' : 'bg-[#C9A962]/10 text-[#C9A962]'}`}>
                        {m.mode === 'Classroom' ? 'Lab' : 'Virtual'}
                      </span>
                    </div>
                  </div>
                  <h4 className="font-semibold text-sm mb-1.5">{m.name}</h4>
                  <p className="text-[#A0ABC0] text-xs leading-relaxed">{m.topics}</p>
                </motion.div>
              ))}
            </motion.div>

            <motion.div variants={fadeInUp} className="mt-8 text-center p-4 bg-[#1A2A40] rounded-lg border border-[#2A3648]">
              <p className="text-[#A0ABC0] text-sm"><strong className="text-white">Total:</strong> 17 Modules &middot; 41 Sessions &middot; 82 Hours &middot; 28 Classroom + 13 Virtual &middot; 4 Mini Projects + 2 Main Projects</p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Highlights & Outcomes */}
      <section id="outcomes" className="section theme-light tint-sky scroll-mt-24">
        <div className="container-custom">
          <motion.div initial="initial" whileInView="animate" viewport={viewportOnce} variants={staggerContainer}>
            <motion.div variants={fadeInUp} className="text-center mb-14">
              <p className="text-[#C9A962] text-sm uppercase tracking-[0.2em] mb-4">Highlights & Outcomes</p>
              <h2 className="text-3xl md:text-4xl font-bold">What You'll Achieve</h2>
            </motion.div>

            <div className="grid lg:grid-cols-3 gap-8">
              {/* Outcomes */}
              <motion.div variants={fadeInUp} className="p-8 bg-[#0A1628] rounded-xl border border-[#2A3648]">
                <div className="flex items-center gap-3 mb-6">
                  <TrendingUp className="text-[#C9A962]" size={22} />
                  <h3 className="font-bold text-lg">Program Outcomes</h3>
                </div>
                <ul className="space-y-3">
                  {OUTCOMES.map((o, i) => (
                    <li key={`o-${i}`} className="flex items-start gap-3 text-[#A0ABC0] text-sm">
                      <CheckCircle className="text-[#C9A962] flex-shrink-0 mt-0.5" size={14} />
                      <span>{o}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>

              {/* Roles */}
              <motion.div variants={fadeInUp} className="p-8 bg-[#0A1628] rounded-xl border border-[#2A3648]">
                <div className="flex items-center gap-3 mb-6">
                  <Briefcase className="text-[#C9A962]" size={22} />
                  <h3 className="font-bold text-lg">Roles You Can Pursue</h3>
                </div>
                <ul className="space-y-3">
                  {ROLES.map((r, i) => (
                    <li key={`r-${i}`} className="flex items-center gap-3 text-[#A0ABC0] text-sm">
                      <ChevronRight className="text-[#C9A962] flex-shrink-0" size={14} />
                      <span>{r}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>

              {/* Skills */}
              <motion.div variants={fadeInUp} className="p-8 bg-[#0A1628] rounded-xl border border-[#2A3648]">
                <div className="flex items-center gap-3 mb-6">
                  <Zap className="text-[#C9A962]" size={22} />
                  <h3 className="font-bold text-lg">Key Skills</h3>
                </div>
                <ul className="space-y-3">
                  {KEY_SKILLS.map((s, i) => (
                    <li key={`s-${i}`} className="flex items-center gap-3 text-[#A0ABC0] text-sm">
                      <CheckCircle className="text-[#C9A962] flex-shrink-0" size={14} />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>

            {/* Who Should Enroll */}
            <motion.div variants={fadeInUp} className="mt-12 grid md:grid-cols-3 gap-6">
              {[
                { id: 'who-students', icon: GraduationCap, title: "University Students", desc: "Final year or pre-final year students looking to build AI skills before entering the workforce." },
                { id: 'who-pros', icon: Briefcase, title: "Working Professionals", desc: "Early-career professionals seeking to upskill and transition into AI/ML roles." },
                { id: 'who-inst', icon: Globe, title: "Academic Institutions", desc: "Universities and colleges looking to offer industry-aligned AI certification to their students." },
              ].map((item) => (
                <div key={item.id} className="p-6 bg-[#0A1628] rounded-xl border border-[#2A3648] text-center">
                  <div className="icon-container mx-auto mb-4 w-14 h-14"><item.icon className="text-[#C9A962]" size={24} /></div>
                  <h4 className="font-bold mb-2">{item.title}</h4>
                  <p className="text-[#A0ABC0] text-sm">{item.desc}</p>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      <PhotoBand
        src={PHOTOS.code}
        eyebrow="Learn by Building"
        title="Industry tools, real datasets, deployable projects"
        text="Every module pairs concepts with hands-on labs so learners leave with a portfolio, not just a certificate."
        testId="ac-band-stack"
      />

      {/* Technology Stack */}
      <section className="section bg-[#0A1628]">
        <div className="container-custom">
          <motion.div initial="initial" whileInView="animate" viewport={viewportOnce} variants={staggerContainer}>
            <motion.div variants={fadeInUp} className="text-center mb-12">
              <p className="text-[#C9A962] text-sm uppercase tracking-[0.2em] mb-4">Technology Stack</p>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Industry-Standard Tools & Frameworks</h2>
              <p className="text-[#A0ABC0] max-w-2xl mx-auto">All tools used are industry-standard and actively used by top AI/ML teams globally. Learners gain practical, job-ready experience.</p>
            </motion.div>
            <motion.div variants={fadeInUp} className="flex flex-wrap justify-center gap-4 mb-10">
              {TECH_STACK.map((t) => (
                <div key={t.name} className="px-5 py-3 bg-[#121E30] border border-[#2A3648] rounded-lg text-sm font-medium text-white hover:border-[#C9A962]/40 transition-colors">
                  {t.name}
                </div>
              ))}
            </motion.div>
            <motion.div variants={fadeInUp} className="grid md:grid-cols-3 gap-6">
              {[
                { icon: BookOpen, title: "Beginner Friendly", desc: "No prior coding experience required for foundational modules. Python is introduced from scratch." },
                { icon: Globe, title: "Industry Relevant", desc: "Tools like PyTorch, Scikit-learn, and OpenCV are used by leading AI companies worldwide." },
                { icon: Code, title: "Hands-On First", desc: "Every tool is taught through live demonstrations, lab exercises, and project-based learning." },
              ].map((item) => (
                <div key={item.title} className="flex items-start gap-4 p-5 bg-[#121E30] rounded-xl border border-[#2A3648]">
                  <div className="w-10 h-10 bg-[#C9A962]/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"><item.icon className="text-[#C9A962]" size={20} /></div>
                  <div>
                    <h4 className="font-bold text-sm mb-1">{item.title}</h4>
                    <p className="text-[#A0ABC0] text-xs">{item.desc}</p>
                  </div>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Projects & Assessment */}
      <section id="projects" className="section theme-light tint-mist scroll-mt-24">
        <div className="container-custom">
          <motion.div initial="initial" whileInView="animate" viewport={viewportOnce} variants={staggerContainer}>
            <motion.div variants={fadeInUp} className="text-center mb-14">
              <p className="text-[#C9A962] text-sm uppercase tracking-[0.2em] mb-4">Projects & Assessment</p>
              <h2 className="text-3xl md:text-4xl font-bold">Prove Your Skills</h2>
            </motion.div>
            <div className="grid md:grid-cols-2 gap-8">
              <motion.div variants={fadeInUp} className="p-8 bg-[#0A1628] rounded-xl border border-[#2A3648]">
                <h3 className="font-bold text-lg mb-6 flex items-center gap-2"><Code className="text-[#C9A962]" size={20} /> Projects</h3>
                <div className="space-y-4">
                  {[
                    { title: "4 Mini Projects", desc: "Apply AI concepts progressively throughout the program" },
                    { title: "2 Main Projects", desc: "Develop comprehensive AI solutions demonstrating full-stack expertise" },
                    { title: "Project Mentorship", desc: "Guided support from industry experts during development" },
                  ].map((p) => (
                    <div key={p.title} className="flex items-start gap-3">
                      <CheckCircle className="text-[#C9A962] flex-shrink-0 mt-1" size={16} />
                      <div><p className="font-medium text-sm">{p.title}</p><p className="text-[#A0ABC0] text-xs">{p.desc}</p></div>
                    </div>
                  ))}
                </div>
              </motion.div>
              <motion.div variants={fadeInUp} className="p-8 bg-[#0A1628] rounded-xl border border-[#2A3648]">
                <h3 className="font-bold text-lg mb-6 flex items-center gap-2"><Award className="text-[#C9A962]" size={20} /> Certification Criteria</h3>
                <div className="space-y-5">
                  <div className="p-4 bg-[#121E30] rounded-lg border border-[#2A3648]">
                    <p className="font-semibold text-sm text-[#C9A962] mb-2">Certificate of Participation</p>
                    <p className="text-[#A0ABC0] text-xs">75% attendance &middot; Project completion optional</p>
                  </div>
                  <div className="p-4 bg-[#121E30] rounded-lg border border-[#C9A962]/30">
                    <p className="font-semibold text-sm text-[#C9A962] mb-2">Certificate of Course Completion</p>
                    <p className="text-[#A0ABC0] text-xs">75% attendance &middot; Mandatory projects &middot; 75% exam pass</p>
                  </div>
                  <div className="p-4 bg-[#121E30] rounded-lg border border-[#2A3648]">
                    <p className="font-semibold text-sm mb-2">Important Timelines</p>
                    <div className="text-[#A0ABC0] text-xs space-y-1">
                      <p>Mini Project: Within 15 days of course completion</p>
                      <p>Main Project: Within 30 days of course completion</p>
                      <p>Exam: Within 60 days of course completion</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Program Pathway */}
      <section className="section bg-[#0A1628]">
        <div className="container-custom">
          <motion.div initial="initial" whileInView="animate" viewport={viewportOnce} variants={staggerContainer}>
            <motion.div variants={fadeInUp} className="text-center mb-14">
              <p className="text-[#C9A962] text-sm uppercase tracking-[0.2em] mb-4">Program Pathway</p>
              <h2 className="text-3xl md:text-4xl font-bold">Your AI Catalyst&trade; Journey</h2>
            </motion.div>
            <motion.div variants={fadeInUp} className="grid md:grid-cols-3 gap-6">
              {[
                { phase: "Phase 1", title: "Fundamentals", desc: "AI basics, tools, ethics", detail: "6 modules \u00B7 11 sessions", color: "from-[#C9A962]/20 to-[#C9A962]/5" },
                { phase: "Phase 2", title: "Advanced", desc: "Python, ML, DL, NLP, CV, IoT, Deployment", detail: "11 modules \u00B7 30 sessions", color: "from-[#C9A962]/30 to-[#C9A962]/10" },
                { phase: "Phase 3", title: "Industry Ready", desc: "Certified, mentored, internship-eligible", detail: "Projects + Exam + Certification", color: "from-[#C9A962]/40 to-[#C9A962]/15" },
              ].map((p, i) => (
                <div key={p.phase} className={`p-8 bg-gradient-to-br ${p.color} rounded-xl border border-[#C9A962]/20 text-center relative`}>
                  <span className="text-[#C9A962] text-xs font-semibold uppercase tracking-wider">{p.phase}</span>
                  <h3 className="text-xl font-bold mt-2 mb-2">{p.title}</h3>
                  <p className="text-[#A0ABC0] text-sm mb-3">{p.desc}</p>
                  <p className="text-[#C9A962] text-xs font-medium">{p.detail}</p>
                  {i < 2 && <div className="hidden md:block absolute top-1/2 -right-4 text-[#C9A962] text-2xl">&rarr;</div>}
                </div>
              ))}
            </motion.div>
            <motion.div variants={fadeInUp} className="mt-10 grid md:grid-cols-3 gap-6">
              {[
                { icon: GraduationCap, title: "Semester Elective", desc: "Integrate as a credit-bearing elective across one or two semesters." },
                { icon: Award, title: "Add-On Certification", desc: "Offer as a value-added certification alongside existing degree programs." },
                { icon: Shield, title: "Industry Partnership", desc: "Delivered by QCP \u2014 a Govt. of India recognized, ISO 27001:2022 certified company." },
              ].map((item) => (
                <div key={item.title} className="flex items-start gap-4 p-5 bg-[#121E30] rounded-xl border border-[#2A3648]">
                  <div className="w-10 h-10 bg-[#C9A962]/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"><item.icon className="text-[#C9A962]" size={20} /></div>
                  <div>
                    <h4 className="font-bold text-sm mb-1">{item.title}</h4>
                    <p className="text-[#A0ABC0] text-xs">{item.desc}</p>
                  </div>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      <PhotoBand
        src={PHOTOS.graduation}
        eyebrow="Career Pathways"
        title="From certification to career"
        text="Mentorship, internships and placement support in the advanced phase help learners take the next step."
        testId="ac-band-placement"
      />

      {/* Internship & Placement */}
      <section id="placement" className="section theme-light tint-teal scroll-mt-24">
        <div className="container-custom">
          <motion.div initial="initial" whileInView="animate" viewport={viewportOnce} variants={staggerContainer}>
            <motion.div variants={fadeInUp} className="max-w-3xl mb-12">
              <p className="text-[#C9A962] text-sm uppercase tracking-[0.2em] mb-4">Career Support</p>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Internship & Placement Assistance</h2>
              <p className="text-[#A0ABC0] text-lg">AI Catalyst&trade; certified learners gain access to QCP's exclusive annual Internship & Placement Drive &mdash; a curated opportunity to connect with leading organizations.</p>
            </motion.div>
            <motion.div variants={staggerContainer} className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: Users, title: "Annual Placement Drive", desc: "Dedicated Internship & Placement Drive exclusively for AI Catalyst\u2122 certified participants." },
                { icon: Globe, title: "50+ Partner Organizations", desc: "Access to QCP's network spanning AI, data science, software development, and emerging tech." },
                { icon: BarChart3, title: "Profile Preparation", desc: "Guidance on resume building, portfolio presentation, and interview readiness." },
                { icon: UserCheck, title: "Industry Introductions", desc: "Profile sharing with partner organizations and leading tech companies actively hiring AI talent." },
                { icon: Star, title: "Lifetime Community", desc: "Complimentary lifetime membership to QCP's exclusive community for networking and career opportunities." },
                { icon: Shield, title: "Govt. Recognized", desc: "Certification backed by QCP \u2014 a Govt. of India recognized Ed-Tech & ISO 27001:2022 certified company." },
              ].map((item) => (
                <motion.div key={item.title} variants={fadeInUp} className="p-6 bg-[#0A1628] rounded-xl border border-[#2A3648]">
                  <item.icon className="text-[#C9A962] mb-4" size={22} />
                  <h4 className="font-bold text-sm mb-2">{item.title}</h4>
                  <p className="text-[#A0ABC0] text-xs">{item.desc}</p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Governing Council */}
      <section className="section bg-[#0A1628]">
        <div className="container-custom">
          <motion.div initial="initial" whileInView="animate" viewport={viewportOnce} variants={staggerContainer}>
            <motion.div variants={fadeInUp} className="text-center mb-14">
              <p className="text-[#C9A962] text-sm uppercase tracking-[0.2em] mb-4">Leadership</p>
              <h2 className="text-3xl md:text-4xl font-bold">Governing Council</h2>
            </motion.div>
            <motion.div variants={staggerContainer} className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {COUNCIL.map((m) => (
                <motion.div key={m.id} variants={fadeInUp} className="p-8 bg-[#121E30] rounded-xl border border-[#2A3648]">
                  <h3 className="text-lg font-bold mb-1">{m.name}</h3>
                  <p className="text-[#C9A962] text-sm font-medium mb-3">{m.title}</p>
                  <p className="text-[#A0ABC0] text-sm mb-3">{m.desc}</p>
                  <p className="text-[#A0ABC0] text-xs"><strong className="text-white">Previous:</strong> {m.prev}</p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section theme-light tint-ivory">
        <div className="container-custom">
          <motion.div initial="initial" whileInView="animate" viewport={viewportOnce} variants={staggerContainer}>
            <motion.div variants={fadeInUp} className="text-center mb-14">
              <p className="text-[#C9A962] text-sm uppercase tracking-[0.2em] mb-4">Testimonials</p>
              <h2 className="text-3xl md:text-4xl font-bold">What Our Learners Say</h2>
            </motion.div>

            {/* Stats Row */}
            <motion.div variants={fadeInUp} className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
              {[
                { value: "98%", label: "Learner Satisfaction" },
                { value: "4.8/5", label: "Average Rating" },
                { value: "10,000+", label: "Learners Impacted" },
                { value: "85%", label: "Career Impact" },
              ].map((s) => (
                <div key={s.label} className="text-center p-4 bg-[#0A1628] rounded-lg border border-[#2A3648]">
                  <p className="text-2xl md:text-3xl font-bold text-[#C9A962]">{s.value}</p>
                  <p className="text-[#A0ABC0] text-xs mt-1">{s.label}</p>
                </div>
              ))}
            </motion.div>

            <motion.div variants={staggerContainer} className="grid md:grid-cols-2 gap-6">
              {TESTIMONIALS.map((t) => (
                <motion.div key={t.id} variants={fadeInUp} className="p-6 bg-[#0A1628] rounded-xl border border-[#2A3648]">
                  <p className="text-[#A0ABC0] text-sm mb-4 italic leading-relaxed">&ldquo;{t.text}&rdquo;</p>
                  <div>
                    <p className="font-semibold text-sm">{t.name}</p>
                    <p className="text-[#C9A962] text-xs">{t.role}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section bg-gradient-to-br from-[#121E30] to-[#0A1628] relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(#C9A962 1px, transparent 1px), linear-gradient(90deg, #C9A962 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
        <div className="container-custom text-center relative z-10">
          <motion.div initial="initial" whileInView="animate" viewport={viewportOnce} variants={staggerContainer} className="max-w-2xl mx-auto">
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              Ready to Bring AI Catalyst&trade; to <span className="text-gradient">Your Institution</span>?
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-[#A0ABC0] text-lg mb-10">
              Partner with Qbic Catalyst Partners to offer an industry-recognized AI certification that transforms your students&rsquo; career prospects.
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-wrap justify-center gap-4">
              <Link to="/contact" className="btn-primary text-lg px-10 py-4 inline-flex items-center gap-3" data-testid="ac-cta-contact">
                Get in Touch <ArrowRight size={20} />
              </Link>
              <Link to="/programs#ai-catalyst" className="btn-secondary inline-flex items-center gap-2" data-testid="ac-cta-programs">
                View All Programs <ChevronRight size={18} />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
